import type { PunchPhotoInput } from '@/lib/apiClient';
import { uploadToJobFolder } from '@/lib/graphUpload';
// Static, not `await import()`, for the reason SitePhotoComposer gives: a lazy
// chunk failing on a dropped connection mid-upload would escape every try.
import { compressImageSized, uploadExtension } from '@/lib/compressImage';
import { randomId } from '@/lib/randomId';
import { readPhotoMeta, getDeviceLocation, deviceFixApplies, localYmd } from '@/lib/photoMeta';

/**
 * Uploading one punch photo to the Job Folder, the same way site photos do
 * (#560): read capture time and GPS from the ORIGINAL file (compression strips
 * them), fall back to the phone's position for a recent photo with no GPS,
 * compress, upload with the user's own Graph token, and return the link and
 * metadata the API stores. The file itself stays in SharePoint.
 */

/** Any failure to get a SharePoint token (the same wording as site photos). */
export const SIGN_IN_FAILED =
  'Couldn’t sign in to SharePoint. The sign-in window may have been blocked or closed — allow pop-ups for this site and try again. If it keeps happening, SharePoint access may still be being set up for your account.';

/**
 * A file name that says what the photo is (design handoff: not timestamp-only):
 * `{job}_Punch_{item}_{before|after|video}_{YYYY-MM-DD}_{tag}.{ext}`. `item` is the
 * item number, or `new` for a before photo taken before the item exists. The
 * random tag keeps two photos of one item from ever replacing each other.
 */
export function punchPhotoName(p: {
  jobNumber: string; itemNo: number | null; kind: 'before' | 'after' | 'video'; ymd: string; ext: string; tag?: string;
}): string {
  const item = p.itemNo == null ? 'new' : String(p.itemNo).padStart(3, '0');
  const tag = p.tag ?? randomId().slice(0, 8);
  return `${p.jobNumber}_Punch_${item}_${p.kind}_${p.ymd}_${tag}.${p.ext}`
    .replace(/[:/\\*?"<>|]/g, '')
    .replace(/\s+/g, '');
}

/**
 * Upload one photo. Pass the same `tag` on every attempt for one photo: the
 * name is then the same each time, and with 'replace' a retry after a failed
 * or lost reply overwrites its own earlier copy instead of leaving an orphan
 * in the Job Folder (the lesson of #560).
 */
export async function uploadPunchPhoto(o: {
  file: File;
  kind: 'before' | 'after';
  itemNo: number | null;
  jobNumber: string;
  jobFolderUrl: string;
  subfolder: string;
  getToken: () => Promise<string>;
  tag: string;
}): Promise<{ photo: PunchPhotoInput; name: string }> {
  const meta = await readPhotoMeta(o.file);
  const device = meta.latitude == null && deviceFixApplies(meta.takenAt) ? await getDeviceLocation() : null;
  const c = await compressImageSized(o.file);
  const name = punchPhotoName({
    jobNumber: o.jobNumber,
    itemNo: o.itemNo,
    kind: o.kind,
    // The camera's own day, as site photos use (review of #560); a photo
    // with no capture time falls back to the file's time on this device.
    ymd: meta.takenYmd ?? localYmd(new Date(o.file.lastModified || Date.now())),
    ext: uploadExtension(o.file, c.reencoded),
    tag: o.tag,
  });
  const res = await uploadToJobFolder({
    getToken: o.getToken,
    jobFolderUrl: o.jobFolderUrl,
    subfolder: o.subfolder,
    fileName: name,
    file: c.file,
    conflictBehavior: 'replace',
  });
  const loc = meta.latitude != null && meta.longitude != null
    ? { latitude: meta.latitude, longitude: meta.longitude, source: 'photo' as const, accuracyM: undefined }
    : device
      ? { latitude: device.latitude, longitude: device.longitude, source: 'device' as const, accuracyM: device.accuracyM }
      : null;
  return {
    name,
    photo: {
      fileName: res.name,
      webUrl: res.webUrl,
      sizeBytes: res.size,
      width: c.width ?? undefined,
      height: c.height ?? undefined,
      takenAt: meta.takenAt?.toISOString(),
      latitude: loc ? Number(loc.latitude.toFixed(6)) : undefined,
      longitude: loc ? Number(loc.longitude.toFixed(6)) : undefined,
      locationSource: loc?.source,
      locationAccuracyM: loc?.accuracyM,
    },
  };
}
